/**
 * Navigation Functions
 *
 * History jumping, cell jumping, and expression filling.
 */

import { appState } from '../state/AppState';
import { session } from './puzzleSession';
import { applyColorScheme } from './domSetup';
import { updateHistoryBar, updateMainMenu } from './menuUpdates';

/**
 * Jump to a specific history point (called from history bar links).
 * This is the TypeScript equivalent of the legacy elozmenyUgras function.
 * Exposed to window as elozmenyUgras for compatibility with history bar HTML.
 */
export function historyJump(packageId: number): void {
    if (!session.historyManager) return;

    session.historyManager.jumpTo(packageId);
    updateHistoryBar();
    updateMainMenu();

    // Apply color scheme (like legacy initSzinSema)
    if (session.currentPuzzle) {
        applyColorScheme(session.currentPuzzle);
    }
}

/**
 * Jump to a specific cell (called from clue column links).
 * This is the TypeScript equivalent of the legacy ugrasMezore function.
 * Exposed to window as ugrasMezore for compatibility with clue column HTML.
 *
 * @param cellId - The cell ID to jump to
 * @param isHorizontal - Whether to select in horizontal direction (true) or vertical (false)
 */
export function jumpToCell(cellId: number, isHorizontal: boolean): void {
    const element = document.getElementById(`mezo-${cellId}`);
    if (!element || !session.cellEvents) return;

    // Set the direction
    appState.isHorizontal = isHorizontal;

    // Switch to the cell
    session.cellEvents.switchToCell(element);

    // Focus the element
    element.focus();
}

/**
 * Set the expression (fill cells with characters from stored expression).
 * This is the TypeScript equivalent of the legacy setKifejezes function.
 * Exposed to window as setKifejezes for compatibility with clue menu HTML.
 *
 * Called when clicking "Megváltozott kifejezés" link to restore original expression.
 *
 * @param expression - The expression string to fill into the selected cells
 */
export function setExpression(expression: string): void {
    if (
        !session.cellEvents ||
        !session.currentPuzzle ||
        !session.historyManager
    )
        return;

    // Get the full selection (currentSelection + currentCellId, sorted)
    const selection = [...appState.currentSelection];
    if (!selection.includes(appState.currentCellId)) {
        selection.push(appState.currentCellId);
    }
    selection.sort((a, b) => a - b);

    // Only proceed if expression length matches selection length
    if (selection.length !== expression.length) return;

    const currentElement = document.getElementById(
        `mezo-${appState.currentCellId}`,
    );

    // Fill each cell with the corresponding character
    for (let i = 0; i < selection.length; i++) {
        const cellId = selection[i];
        const char = expression[i];
        const element = document.getElementById(`mezo-${cellId}`);
        const cell = session.currentPuzzle.getCell(cellId);

        if (element && cell) {
            element.innerHTML = char;
            cell.setValue(char);
            session.cellEvents.switchToCell(element, true);
        }
    }

    // Switch back to current cell
    if (currentElement) {
        session.cellEvents.switchToCell(currentElement);
    }

    // Save to history
    session.historyManager.save('Kifejezés beírása');
    updateHistoryBar();

    // Focus current element
    currentElement?.focus();
}
